/**
 * Visual condition builder (page-builder + workflow layered expressions) —
 * author an AND/OR list of `field / type / operator / value` rules. It edits a
 * `ConditionTree`; the caller compiles it to ONE expression via
 * `lib/conditionExpr` (so non-developers never type JavaScript for the common
 * cases). Used by `ConditionEditor` (→ `VisibilityEditor`, workflow predicates).
 *
 * **Type-aware (n8n-style).** Each rule carries a value TYPE chip
 * (Text / Number / Date / Boolean / List / Object); the operator menu is scoped
 * to that type (`opsForType`) and the value control adapts (number/date input,
 * or none for the no-value ops). The type is pre-filled from `fieldTypes` when a
 * known binding is picked — but always overridable, because in an un-executed
 * workflow we have no sample JSON to infer from.
 *
 * Layout: each rule is a compact card — the FIELD is a dropdown of the page's
 * real bindings (so you pick `$queries.orders.rows[0].stato`, never type it),
 * with a "Custom…" escape; type + operator + value stack below so nothing is
 * cramped in the narrow inspector.
 */
import { Plus, X } from "lucide-react";

import {
  effectiveType,
  opNeedsValue,
  opsForType,
  VALUE_TYPES,
  type ConditionOp,
  type ConditionRule,
  type ConditionTree,
  type ValueType,
} from "@/lib/conditionExpr";

import { type KitOption, Segmented, Select } from "./inspectorKit";

const TYPE_OPTIONS: KitOption[] = VALUE_TYPES.map((t) => ({ value: t.value, label: t.label }));
const INPUT =
  "min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs font-mono";
const CUSTOM = "__custom";

/** Value control kind for an op under a type. `range` → two-input (min/max). */
function valueKindFor(type: ValueType, op: ConditionOp): "text" | "number" | "date" | "range" {
  return opsForType(type).find((o) => o.op === op)?.valueKind ?? "text";
}

export function ConditionBuilder({
  value,
  onChange,
  fields = [],
  fieldTypes = {},
}: {
  value: ConditionTree;
  onChange: (tree: ConditionTree) => void;
  /** Binding options for the field dropdown (e.g. `$queries.x.rows[0].col`). */
  fields?: string[];
  /** Binding → value type, for pre-filling the type chip when a field is picked
   *  (default Text when unknown — no sample to infer from in a workflow). */
  fieldTypes?: Record<string, ValueType>;
}) {
  const rules = value.rules;
  const patch = (next: Partial<ConditionTree>) => onChange({ ...value, ...next });
  const setRule = (i: number, r: Partial<ConditionRule>) =>
    patch({ rules: rules.map((x, j) => (j === i ? { ...x, ...r } : x)) });

  /** First operator of a type — the safe default when the type changes. */
  const firstOp = (t: ValueType): ConditionOp => opsForType(t)[0]?.op ?? "eq";

  /** Pick a field: also pre-fill the type from the schema (and re-seat the op
   *  so it's valid for the new type). */
  const pickField = (i: number, field: string) => {
    const known = fieldTypes[field];
    if (known) setRule(i, { field, type: known, op: firstOp(known) });
    else setRule(i, { field });
  };

  const addRule = () => {
    const field = fields[0] ?? "";
    const type = fieldTypes[field] ?? "string";
    patch({ rules: [...rules, { field, type, op: firstOp(type), value: "" }] });
  };
  const removeRule = (i: number) => patch({ rules: rules.filter((_, j) => j !== i) });

  const fieldOptions: KitOption[] = [
    ...fields.map((f) => ({ value: f, label: f })),
    { value: CUSTOM, label: "Custom…" },
  ];

  return (
    <div className="flex flex-col gap-2">
      {rules.length > 1 && (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>Match</span>
          <Segmented
            value={value.combinator}
            options={[
              { value: "and", label: "All (AND)" },
              { value: "or", label: "Any (OR)" },
            ]}
            onChange={(v) => patch({ combinator: v as "and" | "or" })}
          />
        </div>
      )}

      {rules.length === 0 && (
        <p className="text-[11px] text-muted-foreground">
          No conditions — always shown. Add one to show this component only when it holds.
        </p>
      )}

      {rules.map((r, i) => {
        const type = effectiveType(r);
        const known = fields.includes(r.field);
        const isCustom = !known && (r.field !== "" || fields.length === 0);
        const opOptions: KitOption[] = opsForType(type).map((o) => ({
          value: o.op,
          label: o.label,
        }));
        const needsValue = opNeedsValue(type, r.op);
        return (
          <div key={i} className="rounded-md border border-border/60 bg-surface/40 p-2">
            <div className="mb-1.5 flex items-start gap-1.5">
              {fields.length > 0 ? (
                <div className="min-w-0 flex-1">
                  <Select
                    value={isCustom ? CUSTOM : r.field}
                    options={fieldOptions}
                    align="left"
                    onChange={(v) => (v === CUSTOM ? setRule(i, { field: "$page." }) : pickField(i, v))}
                  />
                </div>
              ) : (
                <input
                  className={INPUT}
                  value={r.field}
                  placeholder="$page.field"
                  onChange={(e) => setRule(i, { field: e.target.value })}
                />
              )}
              <button
                type="button"
                aria-label="Remove condition"
                onClick={() => removeRule(i)}
                className="mt-1 shrink-0 rounded p-1 text-muted-foreground hover:text-danger"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Custom field path (power-user escape). */}
            {fields.length > 0 && isCustom && (
              <input
                className={`${INPUT} mb-1.5 w-full`}
                value={r.field}
                placeholder="$page.field"
                onChange={(e) => setRule(i, { field: e.target.value })}
              />
            )}

            {/* Type chip + scoped operator. */}
            <div className="flex items-center gap-1.5">
              <div className="shrink-0">
                <Select
                  value={type}
                  options={TYPE_OPTIONS}
                  align="left"
                  onChange={(v) => {
                    const t = v as ValueType;
                    setRule(i, { type: t, op: firstOp(t) });
                  }}
                />
              </div>
              <div className="min-w-0 flex-1">
                <Select
                  value={r.op}
                  options={opOptions}
                  align="left"
                  onChange={(v) => setRule(i, { op: v as ConditionOp })}
                />
              </div>
            </div>

            {needsValue &&
              (valueKindFor(type, r.op) === "range" ? (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <input
                    className={INPUT}
                    type="number"
                    value={r.value}
                    placeholder="min"
                    onChange={(e) => setRule(i, { value: e.target.value })}
                  />
                  <span className="text-[11px] text-muted-foreground">and</span>
                  <input
                    className={INPUT}
                    type="number"
                    value={r.value2 ?? ""}
                    placeholder="max"
                    onChange={(e) => setRule(i, { value2: e.target.value })}
                  />
                </div>
              ) : (
                <input
                  className={`${INPUT} mt-1.5 w-full`}
                  type={valueKindFor(type, r.op)}
                  value={r.value}
                  placeholder={r.op === "in_last_days" ? "days" : "value"}
                  onChange={(e) => setRule(i, { value: e.target.value })}
                />
              ))}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRule}
        className="flex items-center gap-1 self-start rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-foreground/5"
      >
        <Plus className="h-3 w-3" /> Add condition
      </button>
    </div>
  );
}
